// src/features/stock/pages/StockCreatePage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  ArrowLeft, 
  Save, 
  Package, 
  Hash, 
  DollarSign,
  Calendar,
  MapPin,
  Tag,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { useStock } from '../hooks/useStock';
import { useStockCategories } from '../hooks/useStockCategories';
import { UNITS_OF_MEASURE } from '../constants/stockConstants';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import Input from '../../../shared/components/Input';
import Spinner from '../../../shared/components/Spinner';
import Tooltip from '../../../shared/components/Tooltip';

const schema = yup.object({
  name: yup.string()
    .required('Item name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be less than 100 characters'),
  
  sku: yup.string()
    .nullable()
    .transform(value => value || null)
    .matches(/^[A-Za-z0-9-]+$/, 'SKU can only contain letters, numbers, and hyphens'),
  
  category_id: yup.number()
    .required('Category is required')
    .positive('Please select a valid category'),
  
  description: yup.string()
    .nullable()
    .max(500, 'Description must be less than 500 characters'),
  
  quantity: yup.number()
    .required('Quantity is required')
    .min(0, 'Quantity cannot be negative')
    .typeError('Quantity must be a number'),
  
  unit: yup.string()
    .required('Unit of measure is required'),
  
  reorder_level: yup.number()
    .nullable()
    .transform(value => value || null)
    .min(0, 'Reorder level cannot be negative')
    .typeError('Reorder level must be a number'),
  
  unit_price: yup.number()
    .nullable()
    .transform(value => value || null)
    .min(0, 'Price cannot be negative')
    .typeError('Price must be a number'),
  
  supplier: yup.string()
    .nullable()
    .max(100, 'Supplier name must be less than 100 characters'),
  
  location: yup.string()
    .nullable()
    .max(50, 'Location must be less than 50 characters'),
  
  bin_number: yup.string()
    .nullable()
    .max(20, 'Bin number must be less than 20 characters'),
  
  expiry_date: yup.date()
    .nullable()
    .transform((value, originalValue) => originalValue ? new Date(originalValue) : null)
    .min(new Date(), 'Expiry date must be in the future'),
  
  batch_number: yup.string()
    .nullable()
    .max(50, 'Batch number must be less than 50 characters'),
  
  minimum_stock: yup.number()
    .nullable()
    .transform(value => value || null)
    .min(0, 'Minimum stock cannot be negative'),
  
  maximum_stock: yup.number()
    .nullable()
    .transform(value => value || null)
    .min(0, 'Maximum stock cannot be negative')
    .when('minimum_stock', (minimum_stock, schema) => {
      return minimum_stock ? schema.min(minimum_stock, 'Maximum stock must be greater than minimum stock') : schema;
    }),
  
  tax_rate: yup.number()
    .nullable()
    .transform(value => value || null)
    .min(0, 'Tax rate cannot be negative')
    .max(100, 'Tax rate cannot exceed 100%'),
  
  is_active: yup.boolean()
    .default(true),
  
  requires_prescription: yup.boolean()
    .default(false),
  
  is_controlled_substance: yup.boolean()
    .default(false),
  
  storage_conditions: yup.string()
    .nullable()
    .oneOf(['room_temperature', 'refrigerated', 'frozen', 'controlled'], 'Invalid storage condition'),
  
  notes: yup.string()
    .nullable()
    .max(1000, 'Notes must be less than 1000 characters'),
});

const StockCreatePage = () => {
  const navigate = useNavigate();
  const { createItem } = useStock();
  const { categories, loading: categoriesLoading } = useStockCategories();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      quantity: 0,
      unit: 'pcs',
      is_active: true,
      requires_prescription: false,
      is_controlled_substance: false,
      storage_conditions: 'room_temperature',
    },
    mode: 'onChange',
  });

  const requiresPrescription = watch('requires_prescription');
  const isControlledSubstance = watch('is_controlled_substance');
  const storageCondition = watch('storage_conditions');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await createItem(data);
      navigate('/stock');
    } catch (error) {
      console.error('Failed to create item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/stock');
  };

  const generateSKU = () => {
    const prefix = 'SKU';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const sku = `${prefix}-${timestamp}-${random}`;
    setValue('sku', sku);
    trigger('sku');
  };

  if (categoriesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add New Stock Item</h1>
            <p className="text-gray-600 mt-1">Create a new inventory item</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={Save}
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
          >
            Create Item
          </Button>
        </div>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Item Name"
                {...register('name')}
                error={errors.name?.message}
                icon={Package}
                placeholder="Enter item name"
                required
              />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <Input
                  label="SKU (Stock Keeping Unit)"
                  {...register('sku')}
                  error={errors.sku?.message}
                  icon={Hash}
                  placeholder="Auto-generated if empty"
                />
                <Tooltip content="Generate unique SKU">
                  <button
                    type="button"
                    onClick={generateSKU}
                    className="mt-6 p-2 text-gray-500 hover:text-primary-600 transition-colors"
                  >
                    <HelpCircle className="h-5 w-5" />
                  </button>
                </Tooltip>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                {...register('category_id')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {errors.category_id && (
                <p className="mt-1 text-sm text-red-600">{errors.category_id.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter item description (optional)"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Stock Details */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stock Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Input
                label="Initial Quantity"
                type="number"
                step="0.01"
                {...register('quantity')}
                error={errors.quantity?.message}
                placeholder="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit of Measure <span className="text-red-500">*</span>
              </label>
              <select
                {...register('unit')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {UNITS_OF_MEASURE.map(unit => (
                  <option key={unit.id} value={unit.id}>{unit.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Input
                label="Reorder Level"
                type="number"
                {...register('reorder_level')}
                error={errors.reorder_level?.message}
                placeholder="Minimum stock before reorder"
              />
            </div>

            <div>
              <Input
                label="Unit Price ($)"
                type="number"
                step="0.01"
                {...register('unit_price')}
                error={errors.unit_price?.message}
                icon={DollarSign}
                placeholder="0.00"
              />
            </div>

            <div>
              <Input
                label="Minimum Stock Level"
                type="number"
                {...register('minimum_stock')}
                error={errors.minimum_stock?.message}
                placeholder="Absolute minimum"
              />
            </div>

            <div>
              <Input
                label="Maximum Stock Level"
                type="number"
                {...register('maximum_stock')}
                error={errors.maximum_stock?.message}
                placeholder="Absolute maximum"
              />
            </div>

            <div>
              <Input
                label="Tax Rate (%)"
                type="number"
                step="0.1"
                {...register('tax_rate')}
                error={errors.tax_rate?.message}
                placeholder="0"
              />
            </div>
          </div>
        </Card>

        {/* Storage Information */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Storage Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Location"
                {...register('location')}
                icon={MapPin}
                placeholder="e.g., Main Store, Shelf A"
              />
            </div>

            <div>
              <Input
                label="Bin Number"
                {...register('bin_number')}
                placeholder="e.g., A-01-02"
              />
            </div>

            <div>
              <Input
                label="Batch Number"
                {...register('batch_number')}
                placeholder="Enter batch number"
              />
            </div>

            <div>
              <Input
                label="Expiry Date"
                type="date"
                {...register('expiry_date')}
                error={errors.expiry_date?.message}
                icon={Calendar}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Storage Conditions
              </label>
              <select
                {...register('storage_conditions')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="room_temperature">Room Temperature</option>
                <option value="refrigerated">Refrigerated (2-8°C)</option>
                <option value="frozen">Frozen (-20°C)</option>
                <option value="controlled">Controlled Room Temperature</option>
              </select>
            </div>
          </div>

          {storageCondition === 'refrigerated' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-700">
                This item requires refrigeration. Make sure to store at 2-8°C and monitor temperature regularly.
              </p>
            </div>
          )}

          {storageCondition === 'frozen' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-700">
                This item requires frozen storage. Maintain temperature at -20°C and avoid freeze-thaw cycles.
              </p>
            </div>
          )}
        </Card>

        {/* Supplier Information */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Supplier Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Supplier Name"
                {...register('supplier')}
                icon={Package}
                placeholder="Enter supplier name"
              />
            </div>
          </div>
        </Card>

        {/* Advanced Options */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Advanced Options</h2>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </button>
          </div>

          {showAdvanced && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      {...register('requires_prescription')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Requires Prescription</span>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      {...register('is_controlled_substance')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Controlled Substance</span>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      {...register('is_active')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Active Item</span>
                  </label>
                </div>

                {(requiresPrescription || isControlledSubstance) && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2">Regulatory Requirements</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {requiresPrescription && (
                        <li className="flex items-center gap-2">
                          <span>•</span>
                          <span>Prescription required for dispensing</span>
                        </li>
                      )}
                      {isControlledSubstance && (
                        <>
                          <li className="flex items-center gap-2">
                            <span>•</span>
                            <span>Special handling and storage required</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span>•</span>
                            <span>Double-lock storage required</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span>•</span>
                            <span>Detailed audit trail mandatory</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Any additional information about this item..."
                />
                {errors.notes && (
                  <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={Save}
            isLoading={isSubmitting}
          >
            Create Item
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StockCreatePage;